import { useEffect, useState } from 'react';
import { TrendingUp, TrendingDown, Thermometer } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';

interface PipelineTemperature {
  pipelineTotal: number;
  pipelineLastWeek: number;
  addedThisWeek: number;
  lostThisWeek: number;
  weeklyDelta: number;
  monthlyQuota: number;
  coverage: number;
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/**
 * Story 56.1 — pipeline temperature. The "is the business healthy this
 * week?" hero number on the manager dashboard. Pipeline vs quota plus
 * the 7-day delta and net add/loss for the current week.
 */
export function PipelineTemperatureWidget() {
  const [data, setData] = useState<PipelineTemperature | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get<PipelineTemperature>('/analytics/pipeline-temperature')
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Loading pipeline temperature…
      </div>
    );
  }
  if (!data) return null;

  const deltaPositive = data.weeklyDelta >= 0;
  const coverageRatio = Number(data.coverage); // backend serializes BigDecimal as a number-like

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex items-center gap-2">
        <Thermometer className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Pipeline temperature
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <div className="text-xs text-muted-foreground">Total open</div>
          <div className="mt-1 text-2xl font-bold">{fmt(data.pipelineTotal)}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Coverage vs monthly quota</div>
          <div className="mt-1 text-2xl font-bold">
            {data.monthlyQuota > 0 ? `${(coverageRatio * 100).toFixed(0)}%` : '—'}
          </div>
          {data.monthlyQuota > 0 && (
            <div className="text-xs text-muted-foreground">
              {fmt(data.monthlyQuota)} target
            </div>
          )}
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Added this week</div>
          <div className="mt-1 text-lg font-semibold text-emerald-300">
            +{fmt(data.addedThisWeek)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Lost this week</div>
          <div className="mt-1 text-lg font-semibold text-rose-300">
            -{fmt(data.lostThisWeek)}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-2 border-t pt-3 text-sm">
        {deltaPositive ? (
          <TrendingUp className="h-4 w-4 text-emerald-600" />
        ) : (
          <TrendingDown className="h-4 w-4 text-rose-600" />
        )}
        <span className="text-muted-foreground">
          {deltaPositive ? '+' : ''}
          {fmt(data.weeklyDelta)} vs 7 days ago
        </span>
      </div>
    </div>
  );
}
